import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LayoutTemplate, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { toast } from "sonner";

const CATEGORIES = ["All", "Career", "Business", "Freelance", "Money"];

export default function TemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [cat, setCat] = useState("All");
  const [active, setActive] = useState(null);
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/templates").then(({ data }) => setTemplates(data || [])).catch(() => {});
  }, []);

  const openTemplate = (t) => { setActive(t); setInputs({}); setResult(null); };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/templates/${active.id}/generate`, { inputs });
      setResult(data);
      toast.success("Document generated & saved (+2 points)");
      (data.new_achievements || []).forEach((a) =>
        toast.success(`🏆 Achievement unlocked: ${a.label}`, { description: a.desc })
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const shown = templates.filter((t) => cat === "All" || t.category === cat);

  return (
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10" data-testid="templates-page">
      <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Template Gallery</p>
      <h1 className="mt-2 font-display text-3xl font-medium tracking-tight sm:text-4xl">
        Pro documents in <span className="text-accent">2 questions.</span>
      </h1>
      <p className="mt-3 max-w-xl text-sm text-muted-foreground">
        Pick a template, answer a couple of questions, and the AI writes the full document — personalised to you and saved to your library.
      </p>

      <div className="mt-6 flex flex-wrap gap-1.5">
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`tmpl-cat-${c.toLowerCase()}`}
            className={`rounded-full px-3.5 py-1.5 text-xs transition-colors ${cat === c ? "bg-primary text-primary-foreground" : "bg-secondary text-muted-foreground hover:bg-secondary/80"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {shown.map((t) => (
          <button key={t.id} onClick={() => openTemplate(t)}
            className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 text-left transition-colors hover:border-accent/50"
            data-testid={`template-card-${t.id}`}>
            <div className="flex items-center justify-between">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary"><LayoutTemplate className="h-4 w-4" /></span>
              <span className="rounded-full bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{t.category}</span>
            </div>
            <h2 className="mt-3 font-display text-base">{t.title}</h2>
            <p className="mt-1 flex-1 text-xs text-muted-foreground">{t.description}</p>
            <span className="mt-3 text-xs text-primary">Use template →</span>
          </button>
        ))}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg" data-testid="template-dialog">
          {active && !result && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl">{active.title}</DialogTitle>
                <DialogDescription>{active.description}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {active.fields.map((f) => (
                  <div key={f.key}>
                    <Label>{f.label}</Label>
                    <Input className="mt-1.5" placeholder={f.placeholder}
                      value={inputs[f.key] || ""}
                      onChange={(e) => setInputs((s) => ({ ...s, [f.key]: e.target.value }))}
                      data-testid={`template-field-${f.key}`} />
                  </div>
                ))}
                <Button onClick={generate} disabled={busy} className="w-full bg-accent text-accent-foreground hover:bg-accent/90" data-testid="template-generate-btn">
                  {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> AI is writing… (~30s)</> : <><Sparkles className="mr-2 h-4 w-4" /> Generate my document</>}
                </Button>
              </div>
            </>
          )}
          {active && result && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 font-display text-2xl">
                  <CheckCircle2 className="h-5 w-5 text-emerald-500" /> {result.title} ready!
                </DialogTitle>
                <DialogDescription>Saved to your Documents — edit, download or AI-improve it any time.</DialogDescription>
              </DialogHeader>
              <pre className="max-h-[45vh] overflow-auto whitespace-pre-wrap rounded-xl border border-border bg-background p-4 font-mono text-xs leading-relaxed" data-testid="template-result-content">{result.content}</pre>
              <div className="flex gap-2">
                <Button asChild className="flex-1" data-testid="template-view-docs"><Link to="/documents">Open in Documents →</Link></Button>
                <Button variant="outline" className="flex-1" onClick={() => setResult(null)}>Generate another</Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
